import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCOrganizationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-organization.api-model';
import {CpmCOrganizationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';
import {
    CpmCOrganizationCreateApiSchema,
    CpmCOrganizationUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-organization.api-schemas';
import {OO8OrganizationApiModel} from '../../../../gen/oo8-api-typescript/models/common/oo8-organization.api-model';
import {CpmCOO8ApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-oo8.api-service';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

export class NewEditOrganizationModalConfig {
    constructor(public existing?: CpmCOrganizationApiModel) {}
}

export class NewEditOrganizationModalResult {
    constructor(public item?: CpmCOrganizationApiModel) {}
}

/**
 * New/Edit Organization modal
 */
@Component({
    selector: 'app-new-edit-organization-modal',
    templateUrl: './new-edit-organization-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditOrganizationModalComponent extends IBaseCRUModalComponent<
    CpmCOrganizationApiModel,
    NewEditOrganizationModalComponent,
    NewEditOrganizationModalConfig,
    NewEditOrganizationModalResult
> {
    protected item?: CpmCOrganizationApiModel;
    protected oo8Organizations: OO8OrganizationApiModel[] = [];
    protected enableOo8Feature = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.goTo = () => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([
                CustomerUrlBuilderUtil.customerOrganization(customerUUID, this.item?.uuid)
            ]);
        };

        this.configure(
            'OrganizationApiModel',
            (model: CpmCOrganizationApiModel) => model.name ?? '<Unknown organization>'
        );
        this.config.width = '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.enableOo8Feature = !!this.globalsService.sessionService.getCustomer()?.oo8_connector_uuid;

        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, []),
            oo8_organization_uuid: new FormControl<string | null>(null, [])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading OO8 organizations...',
                f: async (): Promise<void> => {
                    if (!this.enableOo8Feature) return;

                    this.oo8Organizations = (await firstValueFrom(CpmCOO8ApiService.getOO8OrganizationList())).data;
                }
            },
            {
                m: 'Loading the organization...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this.item = await firstValueFrom(
                        CpmCOrganizationApiService.getOrganizationByUUID(
                            this.config.data?.extended?.existing?.uuid ?? ''
                        )
                    );
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the organization...';

            this.form.patchValue(this.item!);
            this.form.updateValueAndValidity();
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCOrganizationCreateApiSchema | CpmCOrganizationUpdateApiSchema = this.isCreationMode
            ? {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value,
                  oo8_organization_uuid: this.form.controls['oo8_organization_uuid'].value
              }
            : {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value,
                  oo8_organization_uuid: this.form.controls['oo8_organization_uuid'].value
              };

        await this.submitCU(
            null,
            CpmCOrganizationApiService.createOrganization,
            CpmCOrganizationApiService.updateOrganization,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCOrganizationApiModel) => new NewEditOrganizationModalResult(resultCRUD)
        );
    }
}
